export interface Subcommand {
  name: string;
  description: string;
  placeholder?: string;
}

export interface Command {
  name: string;
  icon: string;
  description: string;
  subcommands: Subcommand[];
}

export const COMMANDS: Command[] = [
  {
    name: "block",
    icon: "cube",
    description: "Query blocks by number or tag",
    subcommands: [
      { name: "by_number", description: "Get block by number", placeholder: "12345678" },
      { name: "by_tag", description: "Get block by tag", placeholder: "latest" },
      { name: "latest", description: "Get the latest block" },
      { name: "pending", description: "Get pending block" },
    ],
  },
  {
    name: "tx",
    icon: "arrow-right-left",
    description: "Find transaction details",
    subcommands: [
      { name: "by_hash", description: "Get transaction by hash", placeholder: "0x..." },
      { name: "decode", description: "Decode transaction data", placeholder: "0x..." },
      { name: "trace", description: "Get transaction trace", placeholder: "0x..." },
    ],
  },
  {
    name: "address",
    icon: "wallet",
    description: "Lookup address details and balance",
    subcommands: [
      { name: "balance", description: "Get ETH balance", placeholder: "0x... or ENS" },
      { name: "tokens", description: "Get token balances", placeholder: "0x... or ENS" },
      { name: "history", description: "Get transaction history", placeholder: "0x... or ENS" },
      { name: "code", description: "Get contract bytecode", placeholder: "0x..." },
    ],
  },
  {
    name: "token",
    icon: "coins",
    description: "Search tokens and contracts",
    subcommands: [
      { name: "info", description: "Get token info", placeholder: "DAI, USDC, or 0x..." },
      { name: "holders", description: "Get top holders", placeholder: "token address" },
      { name: "transfers", description: "Get recent transfers", placeholder: "token address" },
    ],
  },
  {
    name: "ens",
    icon: "at-sign",
    description: "ENS name lookups",
    subcommands: [
      { name: "resolve", description: "Resolve ENS to address", placeholder: "vitalik.eth" },
      { name: "reverse", description: "Reverse lookup address", placeholder: "0x..." },
      { name: "records", description: "Get ENS records", placeholder: "name.eth" },
    ],
  },
  {
    name: "gas",
    icon: "fuel",
    description: "Gas price information",
    subcommands: [
      { name: "price", description: "Current gas prices" },
      { name: "history", description: "Gas price history" },
      { name: "estimate", description: "Estimate gas for tx", placeholder: "to,value,data" },
    ],
  },
  {
    name: "call",
    icon: "terminal",
    description: "Call contract functions",
    subcommands: [
      { name: "read", description: "Read contract state", placeholder: "address.method(args)" },
      { name: "simulate", description: "Simulate transaction", placeholder: "to,data,value" },
    ],
  },
];

export interface Workflow {
  id: string;
  name: string;
  description: string;
  command: string;
  icon: string;
  source: "official" | "protocol" | "community";
  protocol?: string;
  trending?: boolean;
  installed?: boolean;
}

export const WORKFLOWS: Workflow[] = [
  {
    id: "swap-uniswap",
    name: "Swap on Uniswap",
    description: "Quick swap interface",
    command: "/swap uniswap",
    icon: "repeat",
    source: "official",
    trending: true,
  },
  {
    id: "bridge-base",
    name: "Bridge to Base",
    description: "Bridge ETH to Base L2",
    command: "/bridge base",
    icon: "arrow-right",
    source: "official",
    trending: true,
  },
  {
    id: "check-approvals",
    name: "Check Approvals",
    description: "Review and revoke token approvals",
    command: "/approvals check",
    icon: "shield",
    source: "official",
  },
  {
    id: "uniswap-lp",
    name: "Uniswap LP Position",
    description: "View your liquidity positions",
    command: "/uniswap positions",
    icon: "layers",
    source: "protocol",
    protocol: "Uniswap",
  },
  {
    id: "aave-health",
    name: "Aave Health Factor",
    description: "Monitor your Aave position health",
    command: "/aave health",
    icon: "heart-pulse",
    source: "protocol",
    protocol: "Aave",
  },
];

export interface SearchResult {
  type: "token" | "address" | "ens" | "workflow" | "command";
  id: string;
  name: string;
  description?: string;
  address?: string;
  icon?: string;
  verified?: boolean;
}

// Mock token search results
export const MOCK_TOKENS: SearchResult[] = [
  {
    type: "token",
    id: "dai",
    name: "Dai Stablecoin",
    description: "$1.00",
    address: "0x6b175474e89094c44da98b954eedeac495271d0f",
    verified: true,
  },
  {
    type: "token",
    id: "usdc",
    name: "USD Coin",
    description: "$1.00",
    address: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
    verified: true,
  },
  {
    type: "token",
    id: "uni",
    name: "Uniswap",
    description: "$6.12",
    address: "0x1f9840a85d5af5bf1d1762f925bdaddc4201f984",
    verified: true,
  },
  {
    type: "token",
    id: "weth",
    name: "Wrapped Ether",
    description: "$3,456.00",
    address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2",
    verified: true,
  },
];

export function parseCommand(input: string): {
  command: string | null;
  subcommand: string | null;
  args: string;
} {
  if (!input.startsWith("/")) {
    return { command: null, subcommand: null, args: input };
  }

  const parts = input.slice(1).trim().split(/\s+/);
  const command = parts[0] || null;
  const subcommand = parts[1] || null;
  const args = parts.slice(2).join(" ");

  return { command, subcommand, args };
}

export function getMatchingCommands(input: string): Command[] {
  const { command } = parseCommand(input);
  if (!command) return COMMANDS;

  return COMMANDS.filter((cmd) =>
    cmd.name.toLowerCase().startsWith(command.toLowerCase())
  );
}

export function getMatchingSubcommands(input: string): Subcommand[] {
  const { command, subcommand } = parseCommand(input);
  if (!command) return [];

  const cmd = COMMANDS.find(
    (c) => c.name.toLowerCase() === command.toLowerCase()
  );
  if (!cmd) return [];

  if (!subcommand) return cmd.subcommands;

  return cmd.subcommands.filter((sub) =>
    sub.name.toLowerCase().startsWith(subcommand.toLowerCase())
  );
}
